// src/utils/dateUtils.js

/**
 * Universal date formatter: converts any date (ISO, YYYY-MM-DD, timestamp, Date)
 * into standard DD/MM/YYYY format across the whole application.
 *
 * @param {string|number|Date} input - The raw date to format
 * @param {string} [fallback='—'] - Fallback display if date is invalid or missing
 * @returns {string} Formatted date in DD/MM/YYYY
 */
export const formatDate = (input, fallback = "—") => {
  if (!input) return fallback;

  // Already in DD/MM/YYYY
  if (typeof input === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(input.trim())) {
    return input.trim();
  }

  // Handle YYYY-MM-DD string explicitly to avoid UTC timezone shifts
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
    const [year, month, day] = input.trim().split("-");
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  }

  try {
    const d = new Date(input);
    if (isNaN(d.getTime())) return fallback;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return fallback;
  }
};

/**
 * Convert any date into YYYY-MM-DD format required by HTML <input type="date" />
 */
export const toInputDate = (input) => {
  if (!input) return "";
  if (typeof input === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(input.trim())) {
    const [day, month, year] = input.trim().split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
    return input.trim();
  }
  try {
    const d = new Date(input);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

export default formatDate;
